import type { DocumentAiAssistPayload } from "@/api/api"

type ModuleRouteRef = {
  id: string
  path: string
}

/**
 * Module route id of the page the canvas is on.
 *
 * Longest path match among the enabled routes, using the same prefix rule as
 * route overrides, so the id and `useWebSearchEnabled` describe one route.
 * Returns that route's id and ignores `overrideSessionType` (session type is a
 * different contract). `/documents` is not a module — a root path `/` would
 * otherwise prefix-match it — and any other unmatched path returns null so the
 * request omits `route_id`.
 */
export function resolveDocumentAiAssistRouteId(
  routes: readonly ModuleRouteRef[],
  pathname: string
): string | null {
  if (pathname === "/documents" || pathname.startsWith("/documents/")) {
    return null
  }

  const currentRoute = routes
    .filter((route) => pathname.startsWith(route.path))
    .sort((a, b) => b.path.length - a.path.length)
    .at(0)

  return currentRoute ? currentRoute.id : null
}

/**
 * Route fields for a document AI-assist request (#3332).
 *
 * The server only offers web search when it knows the route, so it can enforce
 * access to it and apply the route's `disableWebSearch` lock. Without a
 * resolvable route both fields are left out and the request keeps the previous
 * behaviour (no web search).
 */
export function documentAiAssistRouteFields(
  routeId: string | null,
  webSearchEnabled: boolean
): Pick<DocumentAiAssistPayload, "route_id" | "use_web_search"> {
  if (!routeId) return {}
  return { route_id: routeId, use_web_search: webSearchEnabled }
}
